import { Component, type ReactNode } from 'react';
import { isBetterAuthSessionError } from '../lib/auth-errors';
import { isPublicAuthPath } from '../lib/auth-path';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isAuthError: boolean;
}

/**
 * Catches errors thrown synchronously during React rendering and redirects
 * to /login for auth errors, or shows a fallback UI for others.
 */
export class AuthErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isAuthError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, isAuthError: false };
  }

  componentDidCatch(error: Error) {
    console.error('[AuthErrorBoundary] Caught render error:', error);

    if (
      isBetterAuthSessionError(error) &&
      !isPublicAuthPath(window.location.pathname)
    ) {
      window.location.href = '/login';
      return;
    }

    // Non-auth error — update state to show fallback UI
    this.setState({ isAuthError: false });
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isAuthError) {
        return null;
      }
      return (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
          <h2>Something went wrong</h2>
          <details style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
            <summary>Details</summary>
            <p>An unexpected error occurred. If this keeps happening, try clearing your browser data.</p>
          </details>
          <button onClick={() => window.location.reload()} style={{ marginTop: '1rem', padding: '0.5rem 1rem', cursor: 'pointer' }}>
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
